<template>
  <v-dialog v-model="isVisible" persistent max-width="600px">
    <v-card>
      <v-card-title>
        <span class="headline">{{ isEditMode ? "Edit " : "Add New " }} Patient</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="fields.firstName"
                required
                label="First Name *"
                :error="!(fields.firstName && fields.firstName.length) && isFormDirty"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="fields.lastName"
                required
                label="Last Name *"
                :error="!(fields.lastName && fields.lastName.length) && isFormDirty"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="fields.cellNumber"
                v-mask="'(###) ###-####'"
                placeholder="(___) ___-____"
                required
                label="Cell Number *"
                :error="!(fields.cellNumber && fields.cellNumber.length) && isFormDirty"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="fields.email"
                required
                label="Email"
                :error="!(fields.email && fields.email.length) && isFormDirty"
              />
            </v-col>
            <v-col cols="12" sm="12" v-if="!isEditMode">
              <v-select
                v-model="selectedGroup"
                :items="groups"
                label="Group "
                item-text="Name"
                item-value="GroupID"
              />
            </v-col>
          </v-row>
        </v-container>
        <small>* Indicates required field</small>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="secondary" text @click="close">
          Cancel
        </v-btn>
        <v-btn color="primary" text @click="submit">
          {{ isEditMode ? "Update" : "Add" }}
        </v-btn>
      </v-card-actions>
      <v-snackbar v-model="snackbar">
        {{ notification }}

        <template v-slot:action="{ attrs }">
          <v-btn color="pink" text v-bind="attrs" @click="snackbar = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
  /* eslint-disable */
import { VueMaskDirective } from "v-mask";
import Vue from "vue";
import UserHelper from "../../../../utils/user-helper";
import { ApiCalls } from "../../../../api-calls";

Vue.directive("mask", VueMaskDirective);

export default {
  name: "PosPatientsAddEditDialog",
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    selectedPatient: {
      type: Object,
      default: null
    },
    groups: {
      type: Array,
      default: null
    }
  },
  data: () => ({
    snackbar: false,
    notification: "",
    fields: {
      firstName: "",
      lastName: "",
      cellNumber: "",
      groupId: "",
      email: ""
    },
    isFormDirty: false,
    selectedGroup: ""
  }),

  watch: {
    isVisible(val) {
      if (val) {
        this.isFormDirty = false;
        // Dialog opened
        if (this.isEditMode) {
          // Opening in edit mode
          // Assign exisiting entry values
          if (this.selectedPatient.first_name && this.selectedPatient.first_name.length) {
            this.fields.firstName = this.selectedPatient.first_name;
          }
          if (this.selectedPatient.last_name && this.selectedPatient.last_name.length) {
            this.fields.lastName = this.selectedPatient.last_name;
          }
          if (this.selectedPatient.email && this.selectedPatient.email.length) {
            this.fields.email = this.selectedPatient.email;
          }
          if (this.selectedPatient.phone && this.selectedPatient.phone.length) {
            this.fields.cellNumber = this.selectedPatient.phone;
          }
          if (this.selectedPatient.groupId) {
            this.selectedGroup = this.selectedPatient.groupId;
          }
        } else {
          // Opening in add mode
          // Reset all fields
          this.fields = Object.assign({
            firstName: "",
            lastName: "",
            cellNumber: "",
            groupId: "",
            email: ""
          });
          this.selectedGroup = "";
        }
      }
    }
  },

  created() {},

  methods: {
    initialize() {},

    close() {
      this.$emit("on-close");
    },

    parseCellNumberFromMaskFormat(cellNumber) {
      return cellNumber.replace(/[^0-9,]+/g, "");
    },

    isFormValid() {
      this.notification = "";
      if (!this.fields.firstName || !this.fields.firstName.length) {
        this.notification = "Please provide a valid first name";
        return false;
      }
      if (!this.fields.lastName || !this.fields.lastName.length) {
        this.notification = "Please provide a valid last name";
        return false;
      }
      if (
        !this.fields.cellNumber ||
        !this.fields.cellNumber.length ||
        this.parseCellNumberFromMaskFormat(this.fields.cellNumber).length !== 10
      ) {
        this.notification = "Provide a valid 10 digit cell number";
        return false;
      }
      if (!this.fields.email || !this.fields.email.length) {
        this.notification = "Please provide a valid email";
        return false;
      }
      return true;
    },

    async submit() {
      this.isFormDirty = true;
      if (!this.isFormValid()) {
        this.snackbar = true;
        return;
      }
      const activeClinic = UserHelper.getActiveClinic();
      const activePracticeId = activeClinic.PracticeID;
      if (this.isEditMode) {
        // // Update
        const payload = {
          FirstName: this.fields.firstName,
          LastName: this.fields.lastName,
          Email: this.fields.email,
          CellPhone: this.fields.cellNumber.replace(/[^0-9]+/g, ""),
          PracticeID: activePracticeId,
          UserID: activeClinic.UserID
        };
        this.$emit("on-submit-start");

        try {
          const response = await ApiCalls.editPosPatient(payload, this.selectedPatient.id);
          if (response.data) {
            const patient = {
              first_name: response.data.firstName,
              last_name: response.data.lastName,
              email: response.data.email,
              phone: response.data.cellPhone,
              id: response.data.contactID,
              groupId: response.data.groupID
            };
            this.$emit("on-edit-success", patient);
            this.close();
          }
        } catch (error) {
          this.$emit("on-error", error);
        }
      } else {
        // Add
        const payload = {
          FirstName: this.fields.firstName,
          LastName: this.fields.lastName,
          Email: this.fields.email,
          CellPhone: this.fields.cellNumber.replace(/[^0-9]+/g, ""),
          PracticeID: activePracticeId,
          UserID: activeClinic.UserID
        };
        if (this.selectedGroup && this.selectedGroup.toString().length) {
          payload.GroupID = this.selectedGroup;
        }
        this.$emit("on-submit-start");
        try {
          const response = await ApiCalls.createPosPatient(payload);
          if (response.data) {
            const patient = {
              first_name: response.data.firstName,
              last_name: response.data.lastName,
              email: response.data.email,
              phone: response.data.cellPhone,
              id: response.data.contactID,
              groupId: response.data.groupID
            };
            this.$emit("on-add-success", patient);
            this.close();
          }
        } catch (error) {
          this.$emit("on-error", error);
        }
      }
    }
  }
};
</script>
<style scoped>
.placeHoldersWrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 15px 15px;
  margin-top: 25px;
  margin-bottom: 15px;
  background-color: rgb(116, 116, 116);
  border-radius: 8px;
}
.label {
  color: white;
  font-weight: 600;
}

.dropdown {
  display: inline-block;
  position: relative;
}

.dd-button {
  display: inline-block;
  border: 1px solid gray;
  border-radius: 4px;
  padding: 4px 80px 4px 60px;
  background-color: #ffffff;
  cursor: pointer;
  font-size: 1rem;
  white-space: nowrap;
}

.dd-button:after {
  content: "";
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid black;
}

.dd-button:hover {
  background-color: #eeeeee;
}

.dd-input {
  display: none;
}

.dd-menu {
  position: absolute;
  top: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0;
  margin: 0 0 0 0;
  box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
  list-style-type: none;
}

.dd-input + .dd-menu {
  display: none;
}

.dd-input:hover + .dd-menu {
  display: block;
}

.dd-menu li {
  width: 180px;
  padding: 0px 20px;
  cursor: pointer;
  white-space: nowrap;
}

.dd-menu li:hover {
  background-color: #f6f6f6;
}

.dd-menu li a {
  display: block;
  margin: -10px -20px;
  padding: 10px 20px;
}

.dd-menu li.divider {
  padding: 0;
  border-bottom: 1px solid #cccccc;
}
</style>
